import type { ComponentType, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle
} from '@/components/ui/empty'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

interface PageHeaderProps {
    title: ReactNode
    description?: ReactNode
    actions?: ReactNode
    back?: { href: string, label: string }
    className?: string
}

export const PageHeader: FC<PageHeaderProps> = ({ title, description, actions, back, className }): ReactNode => {
    return (
        <div className={cn('mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between print:hidden', className)}>
            <div className='min-w-0'>
                {back && (
                    <Link
                        href={back.href}
                        className='mb-2 inline-flex items-center gap-1 rounded-md text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                    >
                        <ChevronLeft className='size-4' aria-hidden='true' />
                        {back.label}
                    </Link>
                )}

                <h1 className='font-heading text-2xl font-semibold tracking-tight text-balance text-foreground sm:text-[1.7rem]'>
                    {title}
                </h1>

                {description && (
                    <p className='mt-1 text-sm text-muted-foreground'>
                        {description}
                    </p>
                )}
            </div>

            {actions && (
                <div className='flex flex-wrap items-center gap-2'>
                    {actions}
                </div>
            )}
        </div>
    )
}

interface SectionCardProps {
    title?: ReactNode
    description?: ReactNode
    icon?: ComponentType<{ className?: string }>
    actions?: ReactNode
    children: ReactNode
    className?: string
    contentClassName?: string
    id?: string
}

export const SectionCard: FC<SectionCardProps> = ({
    title,
    description,
    icon: Icon,
    actions,
    children,
    className,
    contentClassName,
    id
}): ReactNode => {
    return (
        <section id={id} className={cn('rounded-2xl border bg-card shadow-xs shadow-slate-900/[0.03]', className)}>
            {(title || actions) && (
                <div className='flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3.5 sm:px-5'>
                    <div className='flex min-w-0 items-start gap-3'>
                        {Icon && (
                            <span className='mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground'>
                                <Icon className='size-4' />
                            </span>
                        )}

                        <div className='min-w-0'>
                            {title && (
                                <h2 className='font-heading text-[0.95rem] font-semibold text-foreground'>
                                    {title}
                                </h2>
                            )}

                            {description && (
                                <p className='mt-0.5 text-[0.8rem] text-muted-foreground'>
                                    {description}
                                </p>
                            )}
                        </div>
                    </div>

                    {actions && (
                        <div className='flex flex-wrap items-center gap-2'>
                            {actions}
                        </div>
                    )}
                </div>
            )}

            <div className={cn('p-4 sm:p-5', contentClassName)}>
                {children}
            </div>
        </section>
    )
}

interface EmptyStateProps {
    icon: ComponentType<{ className?: string }>
    title: string
    description?: ReactNode
    action?: ReactNode
    className?: string
}

export const EmptyState: FC<EmptyStateProps> = ({ icon: Icon, title, description, action, className }): ReactNode => {
    return (
        <Empty className={cn('border border-dashed bg-white py-10', className)}>
            <EmptyHeader>
                <EmptyMedia variant='icon' className='size-11 rounded-xl bg-accent text-accent-foreground'>
                    <Icon className='size-5' />
                </EmptyMedia>

                <EmptyTitle className='text-[0.95rem] font-semibold'>
                    {title}
                </EmptyTitle>

                {description && (
                    <EmptyDescription>
                        {description}
                    </EmptyDescription>
                )}
            </EmptyHeader>

            {action && (
                <EmptyContent>
                    {action}
                </EmptyContent>
            )}
        </Empty>
    )
}

export const PageSkeleton: FC = (): ReactNode => {
    return (
        <div aria-busy='true' aria-live='polite' className='space-y-6'>
            <span className='sr-only'>Loading clinic records</span>

            <div className='space-y-2'>
                <Skeleton className='h-4 w-24' />
                <Skeleton className='h-8 w-64' />
            </div>

            <div className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6'>
                {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton key={index} className='h-24 rounded-2xl' />
                ))}
            </div>

            <div className='grid gap-6 lg:grid-cols-3'>
                <Skeleton className='h-80 rounded-2xl lg:col-span-2' />
                <Skeleton className='h-80 rounded-2xl' />
            </div>
        </div>
    )
}

interface OptionItem<T extends string> {
    value: T
    label: string
}

interface SimpleSelectProps<T extends string> {
    value: T
    onValueChange: (value: T) => void
    options: Array<OptionItem<T>>
    id?: string
    placeholder?: string
    className?: string
    size?: 'sm' | 'default'
    ariaLabel?: string
}

export const SimpleSelect = <T extends string>({
    value,
    onValueChange,
    options,
    id,
    placeholder,
    className,
    size = 'default',
    ariaLabel
}: SimpleSelectProps<T>): ReactNode => {
    return (
        <Select
            value={value}
            items={options}
            onValueChange={next => {
                if (next !== null) onValueChange(next as T)
            }}
        >
            <SelectTrigger id={id} size={size} aria-label={ariaLabel} className={cn('w-full', className)}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>

            <SelectContent>
                {options.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

interface SegmentedOption<T extends string> {
    value: T
    label: string
    count?: number
}

interface SegmentedControlProps<T extends string> {
    value: T
    onValueChange: (value: T) => void
    options: Array<SegmentedOption<T>>
    label: string
    className?: string
}

export const SegmentedControl = <T extends string>({
    value,
    onValueChange,
    options,
    label,
    className
}: SegmentedControlProps<T>): ReactNode => {
    return (
        <div role='group' aria-label={label} className={cn('inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-xl bg-muted p-1 no-scrollbar', className)}>
            {options.map(option => {
                const active = option.value === value

                return (
                    <button
                        key={option.value}
                        type='button'
                        aria-pressed={active}
                        onClick={() => onValueChange(option.value)}
                        className={cn(
                            'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-3 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
                            active && 'bg-white text-foreground shadow-xs ring-1 ring-border/70'
                        )}
                    >
                        {option.label}

                        {option.count !== undefined && (
                            <span
                                className={cn(
                                    'min-w-5 rounded-full px-1.5 text-center text-xs tabular-nums',
                                    active ? 'bg-accent text-accent-foreground' : 'bg-white/70 text-muted-foreground'
                                )}
                            >
                                {option.count}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}

interface DetailItemProps {
    label: string
    value: ReactNode
    className?: string
}

export const DetailItem: FC<DetailItemProps> = ({ label, value, className }): ReactNode => {
    return (
        <div className={cn('min-w-0', className)}>
            <dt className='text-xs font-medium text-muted-foreground'>
                {label}
            </dt>

            <dd className='mt-0.5 truncate text-sm font-medium text-foreground'>
                {value}
            </dd>
        </div>
    )
}
